import {
  Box,
  Button,
  Grid,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { FiPlus } from "react-icons/fi";
import { algoliasearch, type SearchClient } from "algoliasearch";
import { useNavigate } from "react-router-dom";
import actions, { type DisplayFullOrganization } from "src/actions";
import { useDialog } from "src/Common/Dialog";
import { useFacebookMetaAdsConnect } from "src/Manager/hooks/useFacebookMetaAdsConnect";
import StatCard from "../Components/StatCard";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import type { CampaignIntent } from "common/interfaces/KYDMarketing";
import type { CampaignMetricsSnapshot } from "src/actions/ads";
import { orgMetricsToDisplay } from "./AdsEngagePage/utils";
import type { AdCampaignSortOption } from "./AdsEngagePage/utils";
import CampaignsTableAlgolia from "./AdsEngagePage/CampaignsTableAlgolia";
import CreateCampaignFlowModal, {
  type GuidedFlowDraft,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import GuidedCreateCampaignFlowScreen from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedCreateCampaignFlowScreen";
import { useManagedCampaigns } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useManagedCampaigns";
import { launchCampaignWithPolling } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/guidedCampaignLaunch";
import { adsCampaignPublishSearch } from "src/Manager/Campaigns/Ads/CampaignDetail/useCampaignPublishPolling";
import type { GuidedCreateCampaignParams } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/guidedFlow/types";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import {
  fetchAdvertiserIdentityInstagramAccounts,
  fetchAdvertiserIdentityPages,
  partnershipIdentityCatalogToEditorState,
  type PartnershipPartnerOptionsLoad,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/fetchPartnershipPartnerOptions";
import { mergePartnerFacebookPagesForPicker } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mapPartnershipPartnerOptionsResponse";
import { useDedupedPartnershipPartnerOptionsRefetch } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useDedupedPartnershipPartnerOptionsRefetch";
import { fetchInstagramAccountsForAdvertiserPage } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/fetchInstagramAccountsForAdvertiserPage";
import { mergeInstagramAccounts } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mergeInstagramAccounts";
import { isManagedAdsPartnerPostsEnabledForOrg } from "src/managedAdsPartnerPostsAccess";
import ProactiveInsightsSection from "./AdsEngagePage/ProactiveInsightsSection";
import { MetaAdsConnectionEmptyState } from "./AdsEngagePage/MetaAdsConnectionEmptyState";
import { KYDScope } from "@common/interfaces/ICognito";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function AdsEngagePage({
  permissions: _permissions = [] as KYDScope[],
}: {
  permissions: KYDScope[];
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [orgMetrics, setOrgMetrics] = useState<CampaignMetricsSnapshot | null>(
    null
  );
  const [isOrgMetricsLoading, setIsOrgMetricsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [algoliaConfig, setAlgoliaConfig] = useState<{
    searchClient: SearchClient;
    indexName: string;
  } | null>(null);
  const [algoliaFailed, setAlgoliaFailed] = useState(false);
  const [sortOrderActive, setSortOrderActive] = useState<AdCampaignSortOption>(
    "updated_at_descending"
  );
  const [sortOrderPast, setSortOrderPast] = useState<AdCampaignSortOption>(
    "updated_at_descending"
  );
  const [metaConnectionStatus, setMetaConnectionStatus] = useState<
    "loading" | "connected" | "not_connected"
  >("loading");
  const [adAccountsStatus, setAdAccountsStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [adAccounts, setAdAccounts] = useState<{ id: string; name: string }[]>(
    []
  );
  const [guidedFlowDraft, setGuidedFlowDraft] =
    useState<GuidedFlowDraft | null>(null);
  const [isGuidedFlowModalOpen, setIsGuidedFlowModalOpen] = useState(false);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [catalogPartnerPages, setCatalogPartnerPages] = useState<FacebookPage[]>(
    []
  );
  const [partnerInstagramAccounts, setPartnerInstagramAccounts] = useState<
    InstagramAccount[]
  >([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    InstagramAccount[]
  >([]);
  const [pixels, setPixels] = useState<{ id: string; name: string }[]>([]);
  const [relatedDataLoading, setRelatedDataLoading] = useState(false);
  const [partnershipOptionsLoading, setPartnershipOptionsLoading] =
    useState(false);
  const [partnershipOptionsError, setPartnershipOptionsError] = useState<
    string | null
  >(null);
  const [partnershipPermissionsLoaded, setPartnershipPermissionsLoaded] =
    useState(false);
  const [advertiserValidationMessages, setAdvertiserValidationMessages] =
    useState<string[]>([]);
  const [metaBusinessId, setMetaBusinessId] = useState<string | undefined>();
  const [guidedPublishStatus, setGuidedPublishStatus] = useState<string | null>(
    null
  );
  const [suggestedIntents, setSuggestedIntents] = useState<CampaignIntent[]>(
    []
  );
  const [organization, setOrganization] =
    useState<DisplayFullOrganization | null>(null);
  const [metaConnectLoading, setMetaConnectLoading] = useState(false);

  const nowUnix = Math.floor(Date.now() / 1000);
  // First table: current/upcoming campaigns.
  const numericFiltersNotPast: (string | string[])[] = [
    ["schedule_end_at_unix = 0", `schedule_end_at_unix >= ${nowUnix}`],
  ];
  const numericFiltersPast: (string | string[])[] = [
    "schedule_end_at_unix > 0",
    `schedule_end_at_unix < ${nowUnix}`,
  ];

  const { pollForResult, searchInterests, searchGeoMarkets } =
    useManagedCampaigns({
      defaultCampaign: null,
      defaultEditingAdset: null,
      pages,
      instagramAccounts,
      onPollingComplete: () => {},
    });

  const partnerPages = useMemo(
    () => mergePartnerFacebookPagesForPicker(pages, catalogPartnerPages),
    [pages, catalogPartnerPages]
  );

  const applyPartnershipIdentityLoad = useCallback(
    (catalog: PartnershipPartnerOptionsLoad) => {
      const slices = partnershipIdentityCatalogToEditorState(catalog);
      setCatalogPartnerPages(slices.partnerPages);
      setPartnerInstagramAccounts(slices.partnerInstagramAccounts);
      setAdvertiserValidationMessages(slices.advertiserValidationMessages);
      setPartnershipPermissionsLoaded(slices.partnershipPermissionsLoaded);
      setMetaBusinessId(slices.metaBusinessId);
      setPartnershipOptionsError(null);
    },
    []
  );

  const { refetch: refetchPartnershipPartnerOptions, catalogByKey } =
    useDedupedPartnershipPartnerOptionsRefetch(
      applyPartnershipIdentityLoad,
      setPartnershipOptionsLoading,
      setPartnershipOptionsError
    );

  const partnerPostsEnabled = isManagedAdsPartnerPostsEnabledForOrg(
    organization?.id
  );

  const refetchRelated = useCallback(
    async (ad_account_id: string) => {
      setRelatedDataLoading(true);
      try {
        const partnershipFetch = partnerPostsEnabled
          ? refetchPartnershipPartnerOptions({ ad_account_id })
          : Promise.resolve();
        const [, pixelsResult, identityPages, identityInstagramAccounts] =
          await Promise.all([
            partnershipFetch,
            actions.ADMIN.listPixels({ ad_account_id }),
            fetchAdvertiserIdentityPages({ ad_account_id }),
            fetchAdvertiserIdentityInstagramAccounts({ ad_account_id }),
          ]);
        setPixels(pixelsResult);
        setPages(identityPages);
        setInstagramAccounts(identityInstagramAccounts);
      } catch (e) {
        console.error("Error fetching related data:", e);
        setPartnershipOptionsError(
          e instanceof Error ? e.message : "Could not load campaign identity data"
        );
      } finally {
        setRelatedDataLoading(false);
      }
    },
    [partnerPostsEnabled, refetchPartnershipPartnerOptions]
  );

  const mergeAdvertiserInstagramRows = useCallback((rows: InstagramAccount[]) => {
    if (!rows.length) return;
    setInstagramAccounts((prev) => mergeInstagramAccounts([...prev, ...rows]));
  }, []);

  const mergePartnerInstagramRows = useCallback((rows: InstagramAccount[]) => {
    if (!rows.length) return;
    setPartnerInstagramAccounts((prev) =>
      mergeInstagramAccounts([...prev, ...rows])
    );
  }, []);

  const fetchInstagramForFacebookPage = useCallback(
    async (facebookPageId: string, adAccountIdOverride?: string) => {
      const adAccountId =
        adAccountIdOverride?.trim() || guidedFlowDraft?.adAccountId?.trim();
      if (!adAccountId) return [];
      try {
        const rows = await fetchInstagramAccountsForAdvertiserPage({
          ad_account_id: adAccountId,
          facebook_page_id: facebookPageId,
        });
        mergeAdvertiserInstagramRows(rows);
        return rows;
      } catch (e) {
        console.error("Error fetching Instagram accounts:", e);
        setPartnershipOptionsError(
          e instanceof Error
            ? e.message
            : "Could not load Instagram accounts"
        );
        return [];
      }
    },
    [guidedFlowDraft?.adAccountId, mergeAdvertiserInstagramRows]
  );

  const handleFlowCreateCampaign = useCallback(
    async (params: GuidedCreateCampaignParams) => {
      return await launchCampaignWithPolling(params, {
        toast,
        pollForResult,
        onStatusUpdate: setGuidedPublishStatus,
        onDone: () => setGuidedPublishStatus(null),
        awaitPolling: false,
      });
    },
    [pollForResult, toast]
  );

  const dialog = useDialog();

  const refetchOrganizationAndMetaState = useCallback(async () => {
    try {
      const org = await actions.ADMIN.fetchOrganization();
      setOrganization(org ?? null);
      const needsLogin = org?.ads_manager?.needs_login ?? true;
      setMetaConnectionStatus(needsLogin ? "not_connected" : "connected");

      if (org && !needsLogin) {
        setAdAccountsStatus("loading");
        try {
          const r = await actions.ADMIN.listAdAccounts?.();
          const list = r?.accounts ?? r ?? [];
          setAdAccounts(Array.isArray(list) ? list : []);
          setAdAccountsStatus("ready");
        } catch {
          setAdAccounts([]);
          setAdAccountsStatus("error");
        }
      } else {
        // No list attempt when disconnected or org missing; clear stale list/error
        setAdAccounts([]);
        setAdAccountsStatus("ready");
      }
    } catch {
      setOrganization(null);
      setMetaConnectionStatus("not_connected");
      setAdAccounts([]);
      setAdAccountsStatus("error");
      return;
    }
  }, []);

  const { triggerFacebookLogin } = useFacebookMetaAdsConnect({
    organization,
    toast,
    dialog,
    onAccessGranted: refetchOrganizationAndMetaState,
    onLoadingChange: setMetaConnectLoading,
  });

  useEffect(() => {
    void refetchOrganizationAndMetaState();
  }, [refetchOrganizationAndMetaState]);

  useLayoutEffect(() => {
    if (guidedFlowDraft?.adAccountId) {
      void refetchRelated(guidedFlowDraft.adAccountId);
    }
  }, [guidedFlowDraft?.adAccountId, refetchRelated]);

  const refetchOrgMetrics = useCallback(async () => {
    setIsOrgMetricsLoading(true);
    try {
      const res = await actions.ADMIN.fetchOrganizationAdsMetrics();
      setOrgMetrics(res.metrics ?? null);
      setSuggestedIntents(res.suggested_intents ?? []);
    } catch (e) {
      // keep previous state
      console.error("Error fetching organization ads metrics:", e);
    } finally {
      setIsOrgMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsOrgMetricsLoading(true);
    actions.ADMIN.fetchOrganizationAdsMetrics()
      .then((res) => {
        if (cancelled) return;
        setOrgMetrics(res?.metrics ?? null);
        setSuggestedIntents(res?.suggested_intents ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsOrgMetricsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (algoliaFailed) return;
    let cancelled = false;
    actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.AD_CAMPAIGNS,
    })
      .then((res) => {
        if (cancelled || !res?.events_config) return;
        const search = algoliasearch(
          res.events_config.app_id,
          res.events_config.api_key
        );
        setAlgoliaConfig({
          searchClient: search,
          indexName: res.events_config.index_name,
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setAlgoliaFailed(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [algoliaFailed]);

  const metricsFromOrg = useMemo(
    () => orgMetricsToDisplay(orgMetrics),
    [orgMetrics]
  );
  const metrics = useMemo(
    () =>
      metricsFromOrg ?? {
        updated_at: undefined,
        displaySpend: "$0",
        displayRoas: "-",
        displayTickets: "0",
        displayCtr: "-",
      },
    [metricsFromOrg]
  );
  const displayMetrics = useMemo(
    () =>
      isOrgMetricsLoading
        ? {
            displaySpend: "-",
            displayRoas: "-",
            displayTickets: "-",
            displayCtr: "-",
          }
        : metrics,
    [isOrgMetricsLoading, metrics]
  );

  const shouldShowMetaAdsConnectionCta =
    metaConnectionStatus === "not_connected" ||
    (metaConnectionStatus === "connected" &&
      adAccountsStatus === "ready" &&
      adAccounts.length === 0);

  const isMetaAdsConfigLoading =
    metaConnectionStatus === "loading" ||
    (metaConnectionStatus === "connected" && adAccountsStatus === "loading");

  const hasAdAccountsError = adAccountsStatus === "error";

  if (isMetaAdsConfigLoading) {
    return (
      <VStack w="100%" py={12} spacing={3}>
        <Spinner />
        <Text color="gray.500" fontSize="sm">
          Loading Meta ads...
        </Text>
      </VStack>
    );
  }

  if (hasAdAccountsError) {
    return (
      <VStack w="100%" py={12} spacing={4}>
        <Text color="red.600" fontSize="md" fontWeight="500">
          Error loading ad accounts
        </Text>
        <Text color="gray.600" fontSize="sm">
          There was an error loading your Meta ad accounts. Please try again.
        </Text>
        <Button
          onClick={refetchOrganizationAndMetaState}
          colorScheme="gray"
          size="sm"
        >
          Retry
        </Button>
      </VStack>
    );
  }

  if (shouldShowMetaAdsConnectionCta) {
    const connectionEmptyReason =
      metaConnectionStatus === "not_connected"
        ? ("needs_login" as const)
        : ("no_ad_accounts" as const);
    const canTriggerMetaLoginFromAds =
      connectionEmptyReason === "needs_login" &&
      !!organization?.ads_manager?.config_id;
    return (
      <MetaAdsConnectionEmptyState
        reason={connectionEmptyReason}
        onConnectMetaAds={
          canTriggerMetaLoginFromAds ? triggerFacebookLogin : undefined
        }
        isConnectMetaAdsLoading={metaConnectLoading}
      />
    );
  }

  const guidedCreateAwaitingAdAccount =
    guidedFlowDraft?.mode === "create" && !guidedFlowDraft.adAccountId;

  if (guidedFlowDraft) {
    return (
      <GuidedCreateCampaignFlowScreen
        draft={guidedFlowDraft}
        adAccounts={adAccounts}
        pages={pages}
        partnerPages={partnerPages}
        partnerInstagramAccounts={partnerInstagramAccounts}
        instagramAccounts={instagramAccounts}
        pixels={pixels}
        isRelatedDataLoading={
          relatedDataLoading || guidedCreateAwaitingAdAccount
        }
        partnershipOptionsLoading={partnershipOptionsLoading}
        partnershipOptionsError={partnershipOptionsError}
        partnershipPermissionsLoaded={partnershipPermissionsLoaded}
        advertiserValidationMessages={advertiserValidationMessages}
        metaBusinessId={metaBusinessId}
        partnershipCatalogByIdentityKey={catalogByKey}
        onRefetchPartnershipPartnerOptions={refetchPartnershipPartnerOptions}
        createUploadUrl={actions.ADMIN.createPublicUploadUrl}
        onBack={() => {
          setGuidedPublishStatus(null);
          setGuidedFlowDraft(null);
        }}
        onGuidedCreateAdAccountResolved={(ad_account_id) => {
          setGuidedFlowDraft((current) => {
            if (!current || current.mode !== "create") return current;
            if (current.adAccountId === ad_account_id) return current;
            return { ...current, adAccountId: ad_account_id };
          });
        }}
        onLaunchSuccess={(result) => {
          if (result?.campaignId) {
            navigate(
              `/campaigns/ads/${result.campaignId}${adsCampaignPublishSearch(
                result.publishUpdateId
              )}`
            );
            return;
          }
          setGuidedPublishStatus(null);
          setGuidedFlowDraft(null);
        }}
        toast={toast}
        onFetchInstagramForFacebookPage={fetchInstagramForFacebookPage}
        onMergeAdvertiserInstagramAccounts={mergeAdvertiserInstagramRows}
        onMergePartnerInstagramAccounts={mergePartnerInstagramRows}
        handleCreateCampaign={handleFlowCreateCampaign}
        publishStatusText={guidedPublishStatus}
        organizationId={organization?.id}
        search={{
          interests: searchInterests,
          geo_markets: searchGeoMarkets,
        }}
      />
    );
  }

  return (
    <VStack w="100%" align="stretch" spacing={6} py={4}>
      <HStack justifyContent="space-between" w="100%" flexWrap="wrap" gap={3}>
        <Text fontSize="32px" fontWeight="400">
          Meta Campaigns
        </Text>
        <Button
          rightIcon={<FiPlus />}
          size="md"
          bg="black"
          color="white"
          _hover={{ bg: "gray.800" }}
          onClick={() => setIsGuidedFlowModalOpen(true)}
        >
          New Ad Campaign
        </Button>
      </HStack>

      <VStack align="stretch" spacing={3}>
        <VStack align="stretch" spacing={0}>
          <HStack spacing={2}>
            <Text fontSize="2xl" fontWeight="600">
              Last 30 Days
            </Text>
            {isOrgMetricsLoading && <Spinner size="sm" />}
          </HStack>
          <Text fontSize="sm" color="gray.500">
            Last updated on{" "}
            {metrics.updated_at
              ? dayjs(metrics.updated_at)
                  .tz(organization?.settings?.timezone || "America/New_York")
                  .format("MMMM D, YYYY")
              : "N/A"}{" "}
            at{" "}
            {metrics.updated_at
              ? dayjs(metrics.updated_at)
                  .tz(organization?.settings?.timezone || "America/New_York")
                  .format("h:mm A z")
              : "N/A"}{" "}
          </Text>
        </VStack>
        <Grid
          templateColumns={{
            base: "1fr 1fr",
            md: "repeat(4, 1fr)",
          }}
          gap={4}
        >
          <StatCard
            title="Spend"
            value={displayMetrics.displaySpend}
            valueFontSize="20px"
          />
          <StatCard
            title="Avg. ROAS"
            value={displayMetrics.displayRoas}
            valueFontSize="20px"
          />
          <StatCard
            title="Checkouts"
            value={displayMetrics.displayTickets}
            valueFontSize="20px"
            isKYD
            highlight
          />
          <StatCard
            title="Avg. CTR"
            value={displayMetrics.displayCtr}
            valueFontSize="20px"
          />
        </Grid>
      </VStack>

      {/* <ProactiveInsightsSection
        intents={suggestedIntents}
        onRefetch={refetchOrgMetrics}
      /> */}

      <Box>
        {loading ? (
          <VStack py={6}>
            <Spinner />
          </VStack>
        ) : algoliaFailed ? (
          <Box
            p={4}
            borderWidth="1px"
            borderRadius="md"
            borderColor="red.200"
            bg="red.50"
          >
            <Text color="red.700" fontSize="sm">
              Search is temporarily unavailable. Please try again later.
            </Text>
          </Box>
        ) : !algoliaConfig ? (
          <VStack py={6}>
            <Spinner />
            <Text color="gray.500" fontSize="sm">
              Setting up search…
            </Text>
          </VStack>
        ) : (
          <CampaignsTableAlgolia
            searchClient={algoliaConfig.searchClient}
            indexName={algoliaConfig.indexName}
            title="Current & upcoming campaigns"
            sortOrder={sortOrderActive}
            onSortChange={setSortOrderActive}
            numericFilters={numericFiltersNotPast}
          />
        )}
      </Box>

      <Box>
        {loading || algoliaFailed || !algoliaConfig ? (
          <Text color="gray.500" fontSize="sm" py={2}>
            {algoliaFailed
              ? "Search unavailable."
              : !algoliaConfig
              ? "Setting up search…"
              : "Loading…"}
          </Text>
        ) : (
          <CampaignsTableAlgolia
            searchClient={algoliaConfig.searchClient}
            indexName={algoliaConfig.indexName}
            title="Past Campaigns"
            sortOrder={sortOrderPast}
            onSortChange={setSortOrderPast}
            numericFilters={numericFiltersPast}
            showPerformanceIndicator={false}
            showLegend={false}
          />
        )}
      </Box>
      <CreateCampaignFlowModal
        isOpen={isGuidedFlowModalOpen}
        onClose={() => setIsGuidedFlowModalOpen(false)}
        adAccounts={adAccounts}
        onDraftReady={(draft) => {
          setGuidedFlowDraft(draft);
          setIsGuidedFlowModalOpen(false);
        }}
      />
    </VStack>
  );
}
